import type {
  OnboardingStepParticipationProperties,
  OnboardingStepPassiveProperties,
  OnboardingStepSkipProperties,
} from '@/onboarding/types'
import type { RouteParamsRaw } from 'vue-router'

/**
 * Constants for onboarding api step keys - received from API
 * These are mapped to a vertical specific step and/or converted to kebab case
 * Sometimes, these are 1:1 with locally defined steps
 * Sometimes these map to a different step identifier via ONBOARDING_MAPPED_API_STEPS
 * Sometimes, these just are snake_case to kebab-case conversions
 */
export enum ApiOnboardingStep {
  ActivitySelector = 'activity_selector',
  StateSelector = 'state_selector',
  Autosubscribe = 'autosubscribe',
}

/**
 * Constants for onboarding step names
 * These are used in marketing events and URL paths
 * Names not present here are not supported
 */
export enum LocalOnboardingStep {
  HuntAreas = 'hunt-areas',
  LandSelector = 'land-selector',
  StateSelector = 'state-selector',
  ModeSelector = 'mode-selector',
  Autosubscribe = 'autosubscribe',
  SupportedArea = 'supported-area',

  //Additional Map Onboarding steps
  Setup = 'setup',
  Location = 'location',
  LocationProcessing = `location-processing`,
  SpeciesLoading = `species-loading`,
  SpeciesProcessing = `species-processing`,
  Land = 'land',
  LandProcessing = `land-processing`,
  Confirmation = 'confirmation',
}

export type UnknownOnboardingStep = ApiOnboardingStep | LocalOnboardingStep

const nonInteractiveSteps = [
  LocalOnboardingStep.Setup,
  LocalOnboardingStep.LocationProcessing,
  LocalOnboardingStep.SpeciesLoading,
  LocalOnboardingStep.SpeciesProcessing,
  LocalOnboardingStep.LandProcessing,
]

/**
 * Is the step in question one that requires user interaction?
 * Determination is based on list above.
 */
export const isInteractiveStep = (step: string) => {
  return !nonInteractiveSteps.includes(step as LocalOnboardingStep)
}

export function isLocalOnboardingStep(
  maybeStep: string
): maybeStep is LocalOnboardingStep {
  return Object.values(LocalOnboardingStep).includes(
    maybeStep as LocalOnboardingStep
  )
}

export function isApiOnboardingStep(
  maybeApiStep: string
): maybeApiStep is ApiOnboardingStep {
  return Object.values(ApiOnboardingStep).includes(
    maybeApiStep as ApiOnboardingStep
  )
}

export type OnboardingLoadingState = 'skip' | 'next' | undefined

export type NextStepOptions = {
  navigate?: boolean
  params?: RouteParamsRaw
}

export type OptionalOnboardingStepEmits = {
  (
    e: 'next-step',
    /**
     * Optional event properties.
     * If these are not provided, no interaction event will be published.
     * This would likely represent moving to the next step without user interaction.
     */
    properties?:
      | OnboardingStepParticipationProperties
      | OnboardingStepSkipProperties,
    /**
     * Additional options to provide regarding how to handle the event
     */
    options?: NextStepOptions
  ): void
  (e: 'option-change', option: string): void
}

export type RequiredOnboardingStepEmits = {
  (
    e: 'next-step',
    /**
     * Optional event properties.
     * If these are not provided, no interaction event will be published.
     * This would likely represent moving to the next step without user interaction.
     */
    properties?: OnboardingStepParticipationProperties,
    /**
     * Additional options to provide regarding how to handle the event
     */
    options?: NextStepOptions
  ): void
  (e: 'option-change', option: string): void
}

export type PassiveOnboardingStepEmits = {
  (
    e: 'next-step',
    /**
     * Optional event properties.
     * If these are not provided, no interaction event will be published.
     * This would likely represent moving to the next step without user interaction.
     */
    properties?: OnboardingStepPassiveProperties,
    /**
     * Additional options to provide regarding how to handle the event
     */
    options?: NextStepOptions
  ): void
}
